// AG-05 -- Selección múltiple para los modales de "Metas/Objetivos Sugeridos por IA": lógica
// GENÉRICA de casillas + "Añadir seleccionadas", reusada por cualquier modal de sugerencias de
// IA (Metas en GoalsManager.vue/SubAreaNecessity.vue, Objetivos en SubAreaGoalCallout.vue) en
// vez de mantener tres copias casi idénticas del mismo flujo (15 sep, ronda 24 -- a pedido del
// usuario: "que esta funcionalidad pueda manejarse... al reusable").
//
// CONTRATO -- el componente que use este mixin debe exponer, con estos nombres EXACTOS (patrón
// "adaptador": el mixin no conoce la forma interna del modal de cada sección):
//
//   computed (requeridos):
//     aiBulkSuggestions        Array<Object> -- las sugerencias vigentes del modal. Cada objeto
//                               debe llevar `_selected: false` ya inicializado al construirse
//                               (ver *Result() de cada componente, ej. applyAiGoalResult).
//     aiBulkModalLoading       { get, set } -- proxy hacia el flag `loading` del modal propio.
//     aiBulkModalLoadingText   { get, set } -- proxy hacia el texto de loading del modal propio.
//     aiBulkModalVisible       { get, set } -- proxy hacia el flag `visible` del modal propio.
//     aiBulkItemNoun           String -- sustantivo singular del ítem ("meta" | "objetivo"),
//                               usado en el texto de progreso ("Guardando <noun> 2 de 4…").
//
//   methods (requeridos):
//     aiBulkIsSelectable(suggestion) -> Boolean -- si la sugerencia trae todo lo necesario para
//                               guardarse sola (mismo criterio que ya usa el botón individual
//                               "Usar esta meta"/"Usar este objetivo").
//     aiBulkSaveOne(suggestion) -> Promise<Boolean> -- guarda UNA sugerencia y devuelve si se
//                               guardó. Debe revisar `this.aiBulkSelectInProgress` si necesita
//                               distinguir un guardado individual de uno en cadena (ej. para no
//                               re-disparar un encadenado hacia el siguiente asistente).
//     aiBulkItemLabel(count) -> String -- nombre del ítem para el toast final, con
//                               singular/plural ya resuelto según `count` (ej. "meta sugerida" /
//                               "metas sugeridas").
//
//   methods (opcional):
//     aiBulkBeforeAll(selected) -- si existe, se llama UNA vez, antes de arrancar la cadena, con
//                               el array completo de sugerencias ya filtrado a las marcadas. Útil
//                               para capturar (antes de la 1ra iteración) algún estado que un
//                               resetState() a mitad de cadena vaya a limpiar.
//     aiBulkBeforeEach(suggestion, index) -- si existe, se llama justo ANTES de aiBulkSaveOne en
//                               cada iteración del guardado masivo. Único caso de uso hoy:
//                               GoalsManager.vue lo usa (junto con aiBulkBeforeAll) para
//                               restaurar newGoal.subArea/strenghtsAndNecessities, que un
//                               resetState() a mitad de cadena deja en null/[] (ver comentario
//                               ahí) -- SubAreaNecessity.vue y SubAreaGoalCallout.vue no lo
//                               necesitan y no lo definen.
//
//   mixins propios del host: debe incluir también `alert` (@/mixin/alert.mixin) -- este mixin
//   usa `this.ShowToast` para el resumen final, y los tres componentes ya lo traen.
export default {
  data() {
    return {
      // true durante TODO aiBulkAddSelected() -- expuesto para que aiBulkSaveOne() (y el
      // saveGoalHandler-equivalente de cada host) sepan que están dentro de un guardado en
      // cadena y supriman efectos que solo tienen sentido en un guardado individual (ej. abrir
      // el siguiente asistente encadenado hacia Objetivos).
      aiBulkSelectInProgress: false,
    };
  },
  computed: {
    aiBulkSelectableSuggestions() {
      return (this.aiBulkSuggestions || []).filter((s) => this.aiBulkIsSelectable(s));
    },
    aiBulkHasSelectable() {
      return this.aiBulkSelectableSuggestions.length > 0;
    },
    aiBulkSelectedCount() {
      return this.aiBulkSelectableSuggestions.filter((s) => s._selected).length;
    },
    aiBulkAllSelected() {
      return this.aiBulkHasSelectable && this.aiBulkSelectableSuggestions.every((s) => s._selected);
    },
  },
  methods: {
    aiBulkToggleSelectAll(checked) {
      this.aiBulkSelectableSuggestions.forEach((s) => {
        s._selected = checked;
      });
    },
    // Guarda, EN CADENA (nunca en paralelo -- los hosts comparten un único form/ref y un único
    // resolver de guardado entre llamadas, así que guardar dos a la vez se pisaría entre sí),
    // todas las sugerencias marcadas con la casilla.
    async aiBulkAddSelected() {
      const selected = this.aiBulkSelectableSuggestions.filter((s) => s._selected);
      if (!selected.length) return;

      this.aiBulkSelectInProgress = true;
      this.aiBulkModalLoading = true;
      let savedCount = 0;

      if (typeof this.aiBulkBeforeAll === 'function') {
        this.aiBulkBeforeAll(selected);
      }

      try {
        for (let i = 0; i < selected.length; i++) {
          this.aiBulkModalLoadingText = `Guardando ${this.aiBulkItemNoun} ${i + 1} de ${selected.length}…`;
          if (typeof this.aiBulkBeforeEach === 'function') {
            this.aiBulkBeforeEach(selected[i], i);
          }
          const saved = await this.aiBulkSaveOne(selected[i]);
          if (saved) savedCount++;
        }
      } finally {
        this.aiBulkSelectInProgress = false;
        this.aiBulkModalLoading = false;
      }

      this.aiBulkModalVisible = false;

      if (savedCount > 0) {
        const label = this.aiBulkItemLabel(savedCount);
        this.ShowToast('', `Se ${savedCount === 1 ? 'agregó' : 'agregaron'} ${savedCount} ${label}.`, 'success');
      }
    },
  },
};
